"use client";

import { useState, type FC } from "react";
import { useTranslations } from "next-intl";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import type { TSupportedLocale } from "libs/i18n/src/lib/i18n.locales";

import { Field } from "@/ui/components/form/Field";
import { Label } from "@/ui/components/form/Label";
import {
  RadioGroupIndicator,
  RadioGroupItem,
} from "@/ui/components/form/RadioGroup";
import { Flex } from "@/ui/components/layout/Flex";
import { Drawer } from "@/ui/components/overlay/Drawer";
import { Icon } from "@/ui/components/primitives/Icon";
import { TextBody } from "@/ui/components/typography/TextBody";
import { cn } from "@/ui/lib/utils";
import {
  FOOTER_COUNTRY_OPTION_CLASS_NAME,
  FOOTER_COUNTRY_OPTION_STATE_CLASS_MAP,
  FOOTER_COUNTRY_OPTIONS,
  type TFooterCountrySelectDrawerProps,
} from "@/ui/components/navigation/Footer/Footer.config";
import {
  getLocalesCurrentFirst,
} from "@/ui/components/navigation/Footer/Footer.utils";

export const FooterCountrySelectDrawer: FC<TFooterCountrySelectDrawerProps> = ({
  open,
  onOpenChange,
  currentLocale,
  onConfirm,
}) => {
  const t = useTranslations("Components.Footer.CountryToggle");
  const [ selectedLocale, setSelectedLocale ] = useState<TSupportedLocale>(currentLocale);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedLocale(currentLocale);
    }
    onOpenChange(isOpen);
  };

  const handleConfirm = () => {
    onOpenChange(false);
    if (selectedLocale !== currentLocale) {
      onConfirm(selectedLocale);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      title={t("title")}
      description={t("description")}
      footer={{ ctas: { primary: { children: t("confirmCta"), onClick: handleConfirm } } }}
    >
      <RadioGroupPrimitive.Root
        asChild
        value={selectedLocale}
        onValueChange={(value) => setSelectedLocale(value as TSupportedLocale)}
        aria-label={t("title")}
      >
        <Flex direction="column" gap="4" data-component="FooterCountrySelectOptions">
          { getLocalesCurrentFirst(currentLocale).map((locale) => {
            const { icon, currency } = FOOTER_COUNTRY_OPTIONS[locale];
            const optionId = `footer-country-option-${locale}`;
            const optionState = locale === selectedLocale ? "selected" : "unselected";

            return (
              <Field
                key={locale}
                htmlFor={optionId}
                data-component="FooterCountrySelectOption"
                className={cn(FOOTER_COUNTRY_OPTION_CLASS_NAME, FOOTER_COUNTRY_OPTION_STATE_CLASS_MAP[optionState])}
              >
                <Icon icon={icon} size="md" className="shrink-0" />
                <span className="flex flex-1 flex-col gap-2">
                  <Label>{ t(`Countries.${locale}`) }</Label>
                  <TextBody tag="span" size={2} isResponsive={false}>{ currency }</TextBody>
                </span>
                <RadioGroupItem id={optionId} value={locale} className="h-5 w-5">
                  <RadioGroupIndicator className="h-2.5 w-2.5" />
                </RadioGroupItem>
              </Field>
            );
          }) }
        </Flex>
      </RadioGroupPrimitive.Root>
      <TextBody size={3} color="secondary" isResponsive={false}>
        { t("switchingCountryDisclaimer") }
      </TextBody>
    </Drawer>
  );
};

FooterCountrySelectDrawer.displayName = "FooterCountrySelectDrawer";
